import React, { useState, useEffect } from "react"
import { Edit } from "lucide-react"
import { Modal } from "react-bootstrap"
import getAppConfig from "~/utils/getAppConfig"
import { apiWorkspaceDealsReporting, apiVenues } from "~/routes"
import PageTitle from "~/library/PageTitle"
import CatchCard from "~/library/CatchCard"
import LoadingWrapper from "~/library/LoadingWrapper"
import { useToast } from "~/utils/Toast"
import type { DealsReportingDashboard as DashboardData } from "~/types/DealsReporting"

const DealsReportingDashboard: React.FC = () => {
    const { venueSlug } = getAppConfig()
    const toast = useToast()
    const [dashboardData, setDashboardData] = useState<DashboardData | null>(null)
    const [isLoading, setIsLoading] = useState(true)

    // Sales Goal modal state
    const [showGoalModal, setShowGoalModal] = useState(false)
    const [salesGoalInput, setSalesGoalInput] = useState("")
    const [isSavingGoal, setIsSavingGoal] = useState(false)

    useEffect(() => {
        fetchDashboard()
    }, [])

    const fetchDashboard = async () => {
        setIsLoading(true)
        try {
            const endpoint = apiWorkspaceDealsReporting.dashboard.path({ venue_slug: venueSlug })
            const response = await fetch(endpoint)
            const data = await response.json()
            setDashboardData(data)
        } catch (error) {
            console.error("Error fetching dashboard:", error)
        } finally {
            setIsLoading(false)
        }
    }

    const handleOpenGoalModal = () => {
        setSalesGoalInput(dashboardData?.salesGoal?.toString() || "0")
        setShowGoalModal(true)
    }

    const handleSaveGoal = async () => {
        setIsSavingGoal(true)

        const data = {
            venueSlug: venueSlug,
            venue: {
                sales_year_goal: parseFloat(salesGoalInput) || 0
            }
        }

        const req = apiVenues.update({ data })

        req.then(() => {
            toast("Sales goal updated successfully", "success")
            setShowGoalModal(false)
            fetchDashboard()
        })
            .catch((res) => {
                const errorMessage = res.body?.errors?.join(", ") || res.message || "Unknown error"
                toast(`Error updating sales goal. ${errorMessage}`, "danger")
            })
            .finally(() => {
                setIsSavingGoal(false)
            })
    }

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value || 0)
    }

    const salesGoal = dashboardData?.salesGoal || 0
    const totalPipelineValue = dashboardData?.pipelineOverview?.totalValue || 0
    const closedWonTotal = dashboardData?.closedWonTotal || 0
    const pipelineVsGoal = totalPipelineValue - salesGoal
    const closedWonVsGoal = closedWonTotal - salesGoal

    return (
        <LoadingWrapper isLoading={isLoading}>
            <PageTitle title="Deals Dashboard" />
            {dashboardData && (
                <div className="row">
                    <div className="col-12 mb-4">
                        <CatchCard
                            title="Pipeline Overview"
                            actionButton={
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={handleOpenGoalModal}
                                >
                                    <Edit size={14} className="me-1" />
                                    Edit Sales Goal
                                </button>
                            }
                        >
                            <div className="row text-center">
                                <div className="col-md-2">
                                    <h3 className="mb-1 text-primary">
                                        {formatCurrency(totalPipelineValue)}
                                    </h3>
                                    <p className="text-muted mb-0 small">Total Pipeline Value</p>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-1 text-info">
                                        {dashboardData.pipelineOverview.totalDeals}
                                    </h3>
                                    <p className="text-muted mb-0 small">Total Deals</p>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-1 text-warning">
                                        {formatCurrency(salesGoal)}
                                    </h3>
                                    <p className="text-muted mb-0 small">Yearly Sales Goal</p>
                                </div>
                                <div className="col-md-2">
                                    <h3 className={`mb-1 ${pipelineVsGoal >= 0 ? 'text-success' : 'text-danger'}`}>
                                        {pipelineVsGoal >= 0 ? '+' : ''}{formatCurrency(pipelineVsGoal)}
                                    </h3>
                                    <p className="text-muted mb-0 small">Pipeline vs Goal</p>
                                </div>
                                <div className="col-md-2">
                                    <h3 className="mb-1 text-success">
                                        {formatCurrency(closedWonTotal)}
                                    </h3>
                                    <p className="text-muted mb-0 small">Closed Won Total</p>
                                </div>
                                <div className="col-md-2">
                                    <h3 className={`mb-1 ${closedWonVsGoal >= 0 ? 'text-success' : 'text-danger'}`}>
                                        {closedWonVsGoal >= 0 ? '+' : ''}{formatCurrency(closedWonVsGoal)}
                                    </h3>
                                    <p className="text-muted mb-0 small">Closed Won vs Goal</p>
                                </div>
                            </div>
                        </CatchCard>
                    </div>

                    <div className="col-md-6 mb-4">
                        <CatchCard title="Pipeline by Stage">
                            {dashboardData.pipelineByStage.length > 0 ? (
                                <table className="table table-sm mb-0">
                                    <thead>
                                    <tr>
                                        <th>Stage</th>
                                        <th className="text-end">Deals</th>
                                        <th className="text-end">Value</th>
                                        <th className="text-end">Weighted</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {dashboardData.pipelineByStage.map((stage) => (
                                        <tr key={stage.id}>
                                            <td className="fw-semibold">{stage.name}</td>
                                            <td className="text-end">{stage.count}</td>
                                            <td className="text-end">{formatCurrency(stage.value)}</td>
                                            <td className="text-end text-success">
                                                {formatCurrency(stage.weightedValue)}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-muted text-center py-3 mb-0">No data available</p>
                            )}
                        </CatchCard>
                    </div>

                    <div className="col-md-6 mb-4">
                        <CatchCard title="Pipeline by Type">
                            {dashboardData.pipelineByType.length > 0 ? (
                                <table className="table table-sm mb-0">
                                    <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th className="text-end">Deals</th>
                                        <th className="text-end">Value</th>
                                        <th className="text-end">Weighted</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {dashboardData.pipelineByType.map((type) => (
                                        <tr key={type.id}>
                                            <td className="fw-semibold">
                                                {type.name}
                                            </td>
                                            <td className="text-end">{type.count}</td>
                                            <td className="text-end">{formatCurrency(type.value)}</td>
                                            <td className="text-end text-success">
                                                {formatCurrency(type.weightedValue)}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-muted text-center py-3 mb-0">No data available</p>
                            )}
                        </CatchCard>
                    </div>

                    <div className="col-md-6 mb-4">
                        <CatchCard title="Pipeline by Expected Close Date">
                            {dashboardData.pipelineByCloseDate.length > 0 ? (
                                <table className="table table-sm mb-0">
                                    <thead>
                                    <tr>
                                        <th>Month</th>
                                        <th className="text-end">Deals</th>
                                        <th className="text-end">Value</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {dashboardData.pipelineByCloseDate.map((date) => (
                                        <tr key={date.month}>
                                            <td className="fw-semibold">{date.month}</td>
                                            <td className="text-end">{date.count}</td>
                                            <td className="text-end">{formatCurrency(date.value)}</td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-muted text-center py-3 mb-0">No data available</p>
                            )}
                        </CatchCard>
                    </div>

                    <div className="col-md-6 mb-4">
                        <CatchCard title="Pipeline by Sales Rep">
                            {dashboardData.pipelineByRep.length > 0 ? (
                                <table className="table table-sm mb-0">
                                    <thead>
                                    <tr>
                                        <th>Sales Rep</th>
                                        <th className="text-end">Deals</th>
                                        <th className="text-end">Value</th>
                                        <th className="text-end">Weighted</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {dashboardData.pipelineByRep.map((rep) => (
                                        <tr key={rep.id}>
                                            <td className="fw-semibold">{rep.name}</td>
                                            <td className="text-end">{rep.count}</td>
                                            <td className="text-end">{formatCurrency(rep.value)}</td>
                                            <td className="text-end text-success">
                                                {formatCurrency(rep.weightedValue)}
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            ) : (
                                <p className="text-muted text-center py-3 mb-0">No sales reps with deals</p>
                            )}
                        </CatchCard>
                    </div>
                </div>
            )}

            {/* Edit Sales Goal Modal */}
            <Modal show={showGoalModal} onHide={() => setShowGoalModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Yearly Sales Goal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label className="form-label">Yearly Sales Goal ($)</label>
                        <input
                            type="number"
                            className="form-control"
                            value={salesGoalInput}
                            onChange={(e) => setSalesGoalInput(e.target.value)}
                            placeholder="500000"
                            step="1000"
                            min="0"
                        />
                        <div className="form-text">
                            Set your team's revenue target for this year
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowGoalModal(false)}
                        disabled={isSavingGoal}
                    >
                        Cancel
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={handleSaveGoal}
                        disabled={isSavingGoal}
                    >
                        {isSavingGoal ? 'Saving...' : 'Save Goal'}
                    </button>
                </Modal.Footer>
            </Modal>
        </LoadingWrapper>
    )
}

export default DealsReportingDashboard